import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import './HonorHall.css';
import { useTranslation } from 'react-i18next';
import { FriendsIcon } from './friends';
import { SettingsMenu } from './settings';
import { useMusic } from './hooks/useMusic';
import { MAIN_MENU_MUSIC_PATH } from './config/mainMenuMusic';
import { playSoundEffect } from './hooks/useSoundEffects';
import { API_URL } from './config/apiConfig';
import { useContainerScale } from './hooks/useViewportScale';
import { TutorialMessageBox, TutorialReplayButton, useMenuTutorialReplay } from './tutorial/index';
import { useTutorialStatus } from './hooks/useTutorialStatus';
import ExploreUnknownMissionDepthDisplay from './components/ExploreUnknownMissionDepthDisplay';
import MenuBackButton from './components/MenuBackButton';
import MenuPaintedButton from './components/MenuPaintedButton';
import { isPlayer1DevUser } from './tutorial/isPlayer1DevUser';
import HideDevElementsForScreenshotButton from './devmode/HideDevElementsForScreenshotButton';
import { useHideDevElementsForScreenshot } from './devmode/useHideDevElementsForScreenshot';
import { buildHonorHallExplorersDevMockRows } from './utils/honorHallExplorersDevMock';
import { buildHonorHallBattleLegendsDevMockRows } from './utils/honorHallBattleLegendsDevMock';
import { buildHonorHallBattleStrategiesDevMockRows } from './utils/honorHallBattleStrategiesDevMock';
import {
  BATTLE_UI_END_TURN_BUTTON_PATH,
  HONOR_HALL_TAB_BUTTON_PATH,
  MENU_PAINTED_SURFACE_FILTER,
} from './config/welcomeAssets';
import './StrategyModal.css';
import { getLocalizedSeasonName } from './utils/honorHallSeasonLocalization';
import { getFactionIconPath, getFactionBoxBackground } from './utils/factionHudDisplay';

const HONOR_HALL_TAB_IDS = Object.freeze([
  'battle-legends',
  'battle-strategies',
  'war-heroes',
  'explorers',
  'asset-strategies',
]);

/**
 * @param {unknown} state
 * @returns {string | null}
 */
function parseHonorHallTabFromLocationState(state) {
  if (state == null || typeof state !== 'object') {
    return null;
  }
  const tab = state.honorHallTab;
  if (tab == null) {
    return null;
  }
  if (typeof tab !== 'string' || !HONOR_HALL_TAB_IDS.includes(tab)) {
    throw new Error(`HonorHall: invalid honorHallTab (${String(tab)})`);
  }
  return tab;
}

function HonorHall({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const initialTab = parseHonorHallTabFromLocationState(location.state) ?? 'battle-legends';
  const pendingShowUserRankRef = useRef(location.state?.honorHallShowUserRank === true);
  const appliedNavStateRef = useRef(false);
  
  // Viewport scaling for responsive design
  const { 
    scale: viewportScale, 
    offsetX: viewportOffsetX, 
    offsetY: viewportOffsetY,
    baseWidth: containerBaseWidth,
    baseHeight: containerBaseHeight,
    isMobile: isContainerMobile
  } = useContainerScale();
  
  useMusic(MAIN_MENU_MUSIC_PATH);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [assetStrategiesData, setAssetStrategiesData] = useState([]);
  const [battleStrategiesData, setBattleStrategiesData] = useState([]);
  const [battleLegendsData, setBattleLegendsData] = useState([]);
  const [explorersData, setExplorersData] = useState([]);
  const [warHeroesData, setWarHeroesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showUserRank, setShowUserRank] = useState(false);
  const [showTutorialHonorHall, setShowTutorialHonorHall] = useState(false);
  const [explorersDevMockActive, setExplorersDevMockActive] = useState(false);
  const [battleLegendsDevMockActive, setBattleLegendsDevMockActive] = useState(false);
  const [battleStrategiesDevMockActive, setBattleStrategiesDevMockActive] = useState(false);
  const [seasonStatus, setSeasonStatus] = useState(null);
  const { shouldShowTutorial, markSeenLocally } = useTutorialStatus(user);
  const { isReplayRequest: isReplayingHonorHallTutorial } = useMenuTutorialReplay('honor_hall', {
    enabled: Boolean(user) && !loading,
    onReplay: () => setShowTutorialHonorHall(true),
  });
  
  const itemsPerPage = 100;
  const isPlayer1Dev = isPlayer1DevUser(user);
  const {
    hide: hideDevElementsForScreenshotNow,
    showDevControls: showPlayer1DevUi,
  } = useHideDevElementsForScreenshot();
  const showHonorHallPlayer1DevControls = isPlayer1Dev && showPlayer1DevUi;

  const handleHideDevElementsForScreenshot = useCallback(() => {
    setBattleLegendsDevMockActive(false);
    setBattleStrategiesDevMockActive(false);
    setExplorersDevMockActive(false);
    hideDevElementsForScreenshotNow();
  }, [hideDevElementsForScreenshotNow]);

  const explorersDisplayData = useMemo(() => {
    if (showHonorHallPlayer1DevControls && explorersDevMockActive) {
      return buildHonorHallExplorersDevMockRows();
    }
    return explorersData;
  }, [showHonorHallPlayer1DevControls, explorersDevMockActive, explorersData]);

  const battleLegendsDisplayData = useMemo(() => {
    if (showHonorHallPlayer1DevControls && battleLegendsDevMockActive) {
      return buildHonorHallBattleLegendsDevMockRows();
    }
    return battleLegendsData;
  }, [showHonorHallPlayer1DevControls, battleLegendsDevMockActive, battleLegendsData]);

  const battleStrategiesDisplayData = useMemo(() => {
    if (showHonorHallPlayer1DevControls && battleStrategiesDevMockActive) {
      return buildHonorHallBattleStrategiesDevMockRows();
    }
    return battleStrategiesData;
  }, [showHonorHallPlayer1DevControls, battleStrategiesDevMockActive, battleStrategiesData]);

  // Determine if current tab shows players or strategies
  const isPlayerTab = activeTab === 'battle-legends' || activeTab === 'explorers' || activeTab === 'war-heroes';
  const isStrategyTab = !isPlayerTab;

  const userPlayerTabRank = useMemo(() => {
    if (!user?.username) return null;
    const name = user.username;
    if (activeTab === 'battle-legends') {
      const i = battleLegendsDisplayData.findIndex((p) => p.username === name);
      return i === -1 ? null : i + 1;
    }
    if (activeTab === 'explorers') {
      const i = explorersDisplayData.findIndex((p) => p.username === name);
      return i === -1 ? null : i + 1;
    }
    if (activeTab === 'war-heroes') {
      const i = warHeroesData.findIndex((p) => p.username === name);
      return i === -1 ? null : i + 1;
    }
    return null;
  }, [activeTab, user?.username, battleLegendsDisplayData, explorersDisplayData, warHeroesData]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (appliedNavStateRef.current) {
      return;
    }
    const tab = parseHonorHallTabFromLocationState(location.state);
    const showRank = location.state?.honorHallShowUserRank === true;
    if (!tab && !showRank) {
      return;
    }
    appliedNavStateRef.current = true;
    if (tab) {
      setActiveTab(tab);
      setCurrentPage(1);
      setSearchTerm('');
      setShowUserRank(false);
    }
    if (showRank) {
      pendingShowUserRankRef.current = true;
    }
  }, [location.state]);

  useEffect(() => {
    if (!loading && pendingShowUserRankRef.current && userPlayerTabRank) {
      const targetPage = Math.ceil(userPlayerTabRank / itemsPerPage);
      setCurrentPage(targetPage);
      setShowUserRank(true);
      pendingShowUserRankRef.current = false;
    }
  }, [loading, userPlayerTabRank]);

  useEffect(() => {
    if (user && !loading && shouldShowTutorial('honor_hall')) {
      const timer = setTimeout(() => setShowTutorialHonorHall(true), 600);
      return () => clearTimeout(timer);
    }
  }, [user, loading, shouldShowTutorial]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [assetStrategiesResponse, battleStrategiesResponse, battleLegendsResponse, explorersResponse, warHeroesResponse, seasonStatusResponse] = await Promise.all([
        fetch(`${API_URL}/honor-hall/asset-strategies`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/honor-hall/battle-strategies`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/honor-hall/battle-legends`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/honor-hall/explorers`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/honor-hall/war-heroes`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/honor-hall/season/status`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (!assetStrategiesResponse.ok || !battleStrategiesResponse.ok || !battleLegendsResponse.ok || !explorersResponse.ok || !warHeroesResponse.ok) {
        throw new Error('Failed to fetch honor hall data');
      }

      const assetStrategies = await assetStrategiesResponse.json();
      const battleStrategies = await battleStrategiesResponse.json();
      const battleLegends = await battleLegendsResponse.json();
      const explorers = await explorersResponse.json();
      const warHeroes = await warHeroesResponse.json();
      if (seasonStatusResponse.ok) {
        setSeasonStatus(await seasonStatusResponse.json());
      } else {
        setSeasonStatus(null);
      }

      // Ensure we have arrays
      setAssetStrategiesData(Array.isArray(assetStrategies) ? assetStrategies : []);
      setBattleStrategiesData(Array.isArray(battleStrategies) ? battleStrategies : []);
      setBattleLegendsData(Array.isArray(battleLegends) ? battleLegends : []);
      setExplorersData(Array.isArray(explorers) ? explorers : []);
      setWarHeroesData(Array.isArray(warHeroes) ? warHeroes : []);
    } catch (error) {
      console.error('Error fetching honor hall data:', error);
      setAssetStrategiesData([]);
      setBattleStrategiesData([]);
      setBattleLegendsData([]);
      setExplorersData([]);
      setWarHeroesData([]);
      setSeasonStatus(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (strategyId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/subscribe-strategy/${strategyId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchData();
      } else {
        const errorData = await response.json();
        alert(`${t('honorHall.subscribeFailed')} ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error subscribing to strategy:', error);
      alert(t('honorHall.subscribeFailed'));
    }
  };

  const handleUnsubscribe = async (strategyId) => {
    if (strategyId == null) {
      alert(t('honorHall.unsubscribeError'));
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/unsubscribe-strategy/${strategyId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchData();
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`${t('honorHall.unsubscribeFailed')}${errorData.error ? `: ${errorData.error}` : ''}`);
      }
    } catch (error) {
      console.error('Error unsubscribing from strategy:', error);
      alert(t('honorHall.unsubscribeFailed'));
    }
  };

  const renderStrategyAction = (strategyId, isOwned, isSubscribed) => {
    if (isOwned) {
      return <span className="owned">{t('honorHall.owned')}</span>;
    }
    if (isSubscribed) {
      return (
        <button
          type="button"
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            handleUnsubscribe(strategyId);
          }}
          className="unsubscribe-button"
        >
          {t('honorHall.unsubscribe')}
        </button>
      );
    }
    return (
      <button
        type="button"
        onClick={() => {
          playSoundEffect('/sounds/click.mp3');
          handleSubscribe(strategyId);
        }}
        className="subscribe-button"
      >
        {t('honorHall.subscribe')}
      </button>
    );
  };

  const renderStrategyCell = (strategyName, factionName) => {
    if (strategyName == null || strategyName === '') {
      return '-';
    }

    const resolvedFactionName =
      typeof factionName === 'string' && factionName.trim() !== '' ? factionName.trim() : null;
    const factionIconPath =
      resolvedFactionName && resolvedFactionName !== 'Unknown'
        ? getFactionIconPath(resolvedFactionName)
        : null;

    return (
      <div className="honor-hall-strategy-cell">
        {resolvedFactionName && resolvedFactionName !== 'Unknown' ? (
          <div
            className="faction-orb"
            style={{ background: getFactionBoxBackground(resolvedFactionName) }}
            title={resolvedFactionName}
          >
            {factionIconPath ? (
              <img
                src={factionIconPath}
                alt=""
                className="faction-orb-icon"
                draggable={false}
                aria-hidden="true"
              />
            ) : null}
          </div>
        ) : null}
        <span>{strategyName}</span>
      </div>
    );
  };

  const getCurrentData = () => {
    switch (activeTab) {
      case 'asset-strategies':
        return assetStrategiesData;
      case 'battle-strategies':
        return battleStrategiesDisplayData;
      case 'battle-legends':
        return battleLegendsDisplayData;
      case 'explorers':
        return explorersDisplayData;
      case 'war-heroes':
        return warHeroesData;
      default:
        return [];
    }
  };

  const formatAverageMissionTime = (avgSeconds) => {
    if (avgSeconds == null || avgSeconds === 0) return '-';
    const totalSeconds = Math.round(Number(avgSeconds));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);
    return parts.join(' ');
  };

  const filteredData = isPlayerTab
    ? (Array.isArray(getCurrentData()) ? getCurrentData() : []).filter((player) => {
        const q = searchTerm.toLowerCase();
        const nameMatch = player.username && player.username.toLowerCase().includes(q);
        if (activeTab === 'explorers') {
          const stratName = player.run_strategy_name;
          const stratMatch =
            typeof stratName === 'string' && stratName.toLowerCase().includes(q);
          return nameMatch || stratMatch;
        }
        return nameMatch;
      })
    : (Array.isArray(getCurrentData()) ? getCurrentData() : []).filter(strategy => 
        (strategy.strategy_name && strategy.strategy_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (strategy.player_name && strategy.player_name.toLowerCase().includes(searchTerm.toLowerCase()))
      );


  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = filteredData.slice(startIndex, endIndex);

  const handleShowMe = () => {
    if (isPlayerTab && userPlayerTabRank) {
      const targetPage = Math.ceil(userPlayerTabRank / itemsPerPage);
      setCurrentPage(targetPage);
      setShowUserRank(true);
    }
  };

  const toggleDevMock = (setter) => {
    playSoundEffect('/sounds/click.mp3');
    setter((active) => !active);
    setCurrentPage(1);
    setShowUserRank(false);
    setSearchTerm('');
  };

  const renderDevMockButton = (isActive, setActive, clearKey, mockKey) => (
    <button
      type="button"
      className="honor-hall-dev-mock-btn"
      onClick={() => toggleDevMock(setActive)}
    >
      {isActive ? t(clearKey) : t(mockKey)}
    </button>
  );
  const switchHonorHallTab = (tabId) => {
    playSoundEffect('/sounds/click.mp3');
    setActiveTab(tabId);
    setCurrentPage(1);
    setSearchTerm('');
  };

  const honorHallTabButtonProps = {
    className: 'honor-hall-tab-button',
    imagePath: HONOR_HALL_TAB_BUTTON_PATH,
    surfaceFilter: MENU_PAINTED_SURFACE_FILTER,
  };

  if (loading) {
    return (
      <div className="honor-hall-viewport-wrapper">
        <div 
          className="honor-hall-fullscreen-background"
          style={{ backgroundImage: 'url(/images/honorhall.png)' }}
        />
        <div 
          className={`honor-hall-viewport-container${isContainerMobile ? ' mobile' : ''}`}
          style={{
            width: `${containerBaseWidth}px`,
            height: `${containerBaseHeight}px`,
            transformOrigin: '0 0',
            transform: `translate(${viewportOffsetX}px, ${viewportOffsetY}px) scale(${viewportScale})`,
          }}
        >
          <div className="honor-hall-container">
            <div className="loading">{t('honorHall.loading')}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="honor-hall-viewport-wrapper">
      {/* Full-screen background - outside viewport-container to cover entire screen */}
      <div 
        className="honor-hall-fullscreen-background"
        style={{ backgroundImage: 'url(/images/honorhall.png)' }}
      />
      
      {/* Viewport container - 1920x1080 space that scales to fit screen */}
      <div 
        className={`honor-hall-viewport-container${isContainerMobile ? ' mobile' : ''}`}
        style={{
          width: `${containerBaseWidth}px`,
          height: `${containerBaseHeight}px`,
          transformOrigin: '0 0',
          transform: `translate(${viewportOffsetX}px, ${viewportOffsetY}px) scale(${viewportScale})`,
        }}
      >
        <div className="honor-hall-container">
      
          {user && (
            <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 1000, display: 'flex', alignItems: 'center' }}>
              <FriendsIcon />
              <SettingsMenu onLogout={() => {
                playSoundEffect('/sounds/click.mp3');
                onLogout();
                navigate('/');
              }} />
              <span style={{ color: 'white', marginRight: '10px' }}>{user}</span>
            </div>
          )}

      <div className="honor-hall-header">
        <h1>{t('honorHall.title')}</h1>
        <MenuBackButton to="/welcome" />
      </div>

      {seasonStatus?.active === false && (
        <p className="honor-hall-season-banner honor-hall-season-banner--inactive">
          {t('honorHall.seasonNotStarted')}
          {showHonorHallPlayer1DevControls ? (
            <span className="honor-hall-season-banner-hint">{t('honorHall.seasonDevHint')}</span>
          ) : null}
        </p>
      )}
      {seasonStatus?.active !== false && seasonStatus?.seasonNumber != null && seasonStatus?.seasonName && (
        <p className="honor-hall-season-banner">
          {t('honorHall.seasonRankingsLine', {
            number: seasonStatus.seasonNumber,
            name: getLocalizedSeasonName(t, seasonStatus.seasonNumber),
          })}
        </p>
      )}

      <div className="table-controls">
        <div>
          <input
            type="text"
            placeholder={isPlayerTab ? t('honorHall.searchPlayers') : t('honorHall.searchStrategies')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {isPlayerTab && userPlayerTabRank && (
            <button onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              handleShowMe();
            }} className="show-me-button">
              {t('honorHall.showMe')} ({t('honorHall.rank')} #{userPlayerTabRank})
            </button>
          )}
          {showHonorHallPlayer1DevControls && activeTab === 'battle-legends' && (
            renderDevMockButton(
              battleLegendsDevMockActive,
              setBattleLegendsDevMockActive,
              'honorHall.devMockBattleLegendsClear',
              'honorHall.devMockBattleLegendsButton'
            )
          )}
          {showHonorHallPlayer1DevControls && activeTab === 'battle-strategies' && (
            renderDevMockButton(
              battleStrategiesDevMockActive,
              setBattleStrategiesDevMockActive,
              'honorHall.devMockBattleStrategiesClear',
              'honorHall.devMockBattleStrategiesButton'
            )
          )}
          {showHonorHallPlayer1DevControls && activeTab === 'explorers' && (
            renderDevMockButton(
              explorersDevMockActive,
              setExplorersDevMockActive,
              'honorHall.devMockExplorersClear',
              'honorHall.devMockExplorersButton'
            )
          )}
        </div>
        
        <div className="tabs">
          <MenuPaintedButton
            {...honorHallTabButtonProps}
            active={activeTab === 'battle-legends'}
            onClick={() => switchHonorHallTab('battle-legends')}
          >
            {t('honorHall.battleLegends')}
          </MenuPaintedButton>
          <MenuPaintedButton
            {...honorHallTabButtonProps}
            active={activeTab === 'battle-strategies'}
            onClick={() => switchHonorHallTab('battle-strategies')}
          >
            {t('honorHall.battleStrategies')}
          </MenuPaintedButton>
          <MenuPaintedButton
            {...honorHallTabButtonProps}
            active={activeTab === 'war-heroes'}
            onClick={() => switchHonorHallTab('war-heroes')}
          >
            {t('honorHall.warHeroes')}
          </MenuPaintedButton>
          <MenuPaintedButton
            {...honorHallTabButtonProps}
            active={activeTab === 'explorers'}
            onClick={() => switchHonorHallTab('explorers')}
          >
            {t('honorHall.explorers')}
          </MenuPaintedButton>
          <MenuPaintedButton
            {...honorHallTabButtonProps}
            active={activeTab === 'asset-strategies'}
            onClick={() => switchHonorHallTab('asset-strategies')}
          >
            {t('honorHall.assetStrategies')}
          </MenuPaintedButton>
        </div>
      </div>

      <div
        className={`table-container${
          activeTab === 'war-heroes' ? ' war-heroes-table' : ''
        }${activeTab === 'explorers' ? ' explorers-table' : ''}${
          activeTab === 'battle-legends' ? ' battle-legends-table' : ''
        }${isStrategyTab ? ' strategy-table' : ''}`}
      >
        {activeTab === 'war-heroes' ? (
          <table className="honor-table war-heroes-table">
            <thead>
              <tr>
                <th>{t('honorHall.rank')}</th>
                <th>{t('honorHall.player')}</th>
                <th>{t('honorHall.averageMissionTime')}</th>
              </tr>
            </thead>
            <tbody>
              {currentData.map((player, index) => (
                <tr 
                  key={player.username}
                  className={showUserRank && player.username === user?.username ? 'highlighted' : ''}
                >
                  <td>{startIndex + index + 1}</td>
                  <td>{player.username}</td>
                  <td>{formatAverageMissionTime(player.avg_duration_seconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : activeTab === 'explorers' ? (
          <table className="honor-table explorers-table">
            <thead>
              <tr>
                <th>{t('honorHall.rank')}</th>
                <th>{t('honorHall.player')}</th>
                <th title={t('exploreUnknownRanking.columnHint')}>
                  {t('honorHall.missionReached')}
                </th>
                <th>{t('honorHall.strategy')}</th>
                <th>{t('honorHall.action')}</th>
              </tr>
            </thead>
            <tbody>
              {currentData.map((player, index) => (
                <tr
                  key={player.username}
                  className={showUserRank && player.username === user?.username ? 'highlighted' : ''}
                >
                  <td>{startIndex + index + 1}</td>
                  <td>{player.username}</td>
                  <td>
                    <ExploreUnknownMissionDepthDisplay
                      missions={player.missions_completed}
                      easyCount={player.easy_missions_completed}
                      showCellHint
                    />
                  </td>
                  <td>{renderStrategyCell(player.run_strategy_name, player.faction_name)}</td>
                  <td>
                    {player.subscribe_strategy_id == null ? (
                      <span className="explorers-no-subscribe">-</span>
                    ) : (
                      renderStrategyAction(
                        player.subscribe_strategy_id,
                        player.is_owned,
                        player.is_subscribed
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : isPlayerTab ? (
          battleLegendsDisplayData.length === 0 && seasonStatus?.active !== false ? (
            <p className="honor-hall-battle-legends-empty">{t('honorHall.battleLegendsEmpty')}</p>
          ) : (
          <table className="honor-table battle-legends-table">
            <thead>
              <tr>
                <th>{t('honorHall.rank')}</th>
                <th>{t('honorHall.player')}</th>
                <th>{t('honorHall.winRate')}</th>
              </tr>
            </thead>
            <tbody>
              {currentData.map((player, index) => (
                <tr 
                  key={player.username}
                  className={showUserRank && player.username === user?.username ? 'highlighted' : ''}
                >
                  <td>{startIndex + index + 1}</td>
                  <td>{player.username}</td>
                  <td>{player.win_rate || 0}%</td>
                </tr>
              ))}
            </tbody>
          </table>
          )
        ) : (
          <table className="honor-table strategy-table">
            <thead>
              <tr>
                <th>{t('honorHall.rank')}</th>
                <th>{t('honorHall.player')}</th>
                <th>{t('honorHall.strategy')}</th>
                <th>{t('honorHall.winRate')}</th>
                <th>{t('honorHall.action')}</th>
              </tr>
            </thead>
            <tbody>
              {currentData.map((strategy, index) => (
                <tr key={strategy.strategy_id != null
                  ? `${strategy.strategy_id}-${strategy.player_id}`
                  : `dev-mock-strategy-${startIndex + index}`}>
                  <td>{startIndex + index + 1}</td>
                  <td>{strategy.player_name}</td>
                  <td>{renderStrategyCell(strategy.strategy_name, strategy.faction_name)}</td>
                  <td>{strategy.win_rate}%</td>
                  <td>
                    {strategy.strategy_id == null ? (
                      <span className="explorers-no-subscribe">-</span>
                    ) : (
                      renderStrategyAction(
                        strategy.strategy_id,
                        strategy.is_owned,
                        strategy.is_subscribed
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="pagination">
        <MenuPaintedButton
          onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          disabled={currentPage === 1}
          leadingArrow
        >
          {t('honorHall.previous')}
        </MenuPaintedButton>
        <span>{t('honorHall.page')} {currentPage} {t('honorHall.of')} {totalPages}</span>
        <MenuPaintedButton
          onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          disabled={currentPage === totalPages}
          trailingArrow
        >
          {t('honorHall.next')}
        </MenuPaintedButton>
      </div>

      {showHonorHallPlayer1DevControls ? (
        <TutorialReplayButton user={user} onReplay={() => setShowTutorialHonorHall(true)} />
      ) : null}

      {showHonorHallPlayer1DevControls ? (
        <HideDevElementsForScreenshotButton
          user={user}
          onHide={handleHideDevElementsForScreenshot}
        />
      ) : null}

      {showTutorialHonorHall && createPortal(
        <TutorialMessageBox
          image="/images/tutorial_wait.png"
          text={t('honorHall.tutorialText')}
          continueButtonImagePath={BATTLE_UI_END_TURN_BUTTON_PATH}
          continueButtonClassName="strategy-modal-close-btn"
          paintedButtonSurfaceFilter={MENU_PAINTED_SURFACE_FILTER}
          continueLabelKey="honorHall.tutorialClose"
          onContinue={async () => {
            if (!isReplayingHonorHallTutorial) {
              const token = localStorage.getItem('token');
              if (token) {
                await fetch(`${API_URL}/tutorial/mark-seen`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                  body: JSON.stringify({ messageKey: 'honor_hall' }),
                });
                markSeenLocally('honor_hall');
              }
            }
            setShowTutorialHonorHall(false);
          }}
          voiceKey="honorHall.tutorialText"
        />,
        document.body
      )}
        </div>
      </div>
    </div>
  );
}

export default HonorHall;
